import React, { useState } from "react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteLayout";
import { useI18n } from "@/lib/i18n";
import { api, rp, fmtDate, API } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function CheckBooking() {
  const { t, lang } = useI18n();
  const [code, setCode] = useState("");
  const [wa, setWa] = useState("");
  const [b, setB] = useState(null);
  const [media, setMedia] = useState([]);

  const search = async () => {
    try {
      const r = await api.get("/public/booking-lookup", { params: { booking_code: code, whatsapp: wa } });
      setB(r.data);
      api.get("/public/media", { params: { booking_code: code, whatsapp: wa } }).then((m) => setMedia(m.data)).catch(() => setMedia([]));
    } catch (e) {
      setB(null);
      setMedia([]);
      toast.error(e?.response?.data?.detail || t("bookingNotFound"));
    }
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-2xl px-5 py-16">
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">
          {t("checkBookingTitle")}
        </h1>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <Input data-testid="lookup-code" className="h-12" placeholder={t("bookingCode")}
            value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          <Input data-testid="lookup-wa" className="h-12" placeholder={t("whatsapp")}
            value={wa} onChange={(e) => setWa(e.target.value)} />
        </div>
        <button data-testid="lookup-btn" onClick={search} className="mt-4 lm-btn-primary rounded-full px-8 py-3.5 text-sm font-semibold">{t("findBooking")}</button>
        {b && (
          <div className="mt-8 lm-card rounded-xl p-6 space-y-2.5 text-sm" data-testid="lookup-result">
            <R l={t("bookingId")} v={b.booking_code} />
            <R l={t("customerName")} v={b.customer_name} />
            <R l={t("rentalDate")} v={fmtDate(b.rental_date, lang)} />
            <R l={t("startTime")} v={b.start_time} />
            <R l={t("package")} v={b.package_name_snapshot} />
            <R l={t("bookingQuantity")} v={`${b.quantity} ${t("bookingUnit")}`} />
            <R l={t("deliveryType")} v={b.delivery_type === "delivery" ? `${b.address || ""} ${b.area || ""} (${b.vehicle_type === "bike" ? t("vehicleBike") : t("vehicleCar")})` : t("selfPickup")} />
            <div className="h-px bg-black/10 my-1" />
            <R l={t("bookingTotal")} v={rp(b.total_payment)} />
            {b.pickup_discount > 0 && <R l={b.pickup_discount_label || t("selfPickupDiscount")} v={`-${rp(b.pickup_discount)}`} />}
            {b.loyalty_discount > 0 && <R l={`${b.loyalty_tier} Member -${b.loyalty_discount_percent}%`} v={`-${rp(b.loyalty_discount)}`} />}
            <R l={t("amountPaid")} v={rp(b.amount_paid)} />
            <R l={t("paymentStatus")} v={t("paymentStatusText")[b.payment_status] || b.payment_status} />
            <R l={t("bookingStatus")} v={t("bookingStatusText")[b.booking_status] || b.booking_status} />
            <R l={t("depositStatus")} v={t("depositStatusText")[b.deposit_status] || b.deposit_status} />
          </div>
        )}
        {b?.deposit_receipt && (
          <div className="mt-6 lm-card rounded-xl p-6 space-y-2.5 text-sm" data-testid="deposit-receipt">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl tracking-tight">{t("depositRefundReceipt")}</h2>
              <span className="text-xs text-ink-soft">{b.deposit_receipt.receipt_no}</span>
            </div>
            <div className="border-y border-black/10 py-3 space-y-1.5" data-testid="receipt-booking-details">
              <div className="text-[11px] uppercase tracking-widest text-ink-soft">{t("bookingDetails")}</div>
              <R
                l={t("bookingCode")}
                v={b.deposit_receipt.booking_details?.booking_code || b.booking_code}
              />
              <R
                l={t("customerName")}
                v={b.deposit_receipt.booking_details?.customer_name || b.customer_name}
              />
              <R l="Hub" v={b.deposit_receipt.booking_details?.hub_name || b.branch_name || "—"} />
              <R
                l={t("rentalDateTime")}
                v={`${fmtDate(
                  b.deposit_receipt.booking_details?.rental_date || b.rental_date,
                  lang,
                )} · ${b.deposit_receipt.booking_details?.start_time || b.start_time || "—"}`}
              />
              <R
                l={t("package")}
                v={`${b.deposit_receipt.booking_details?.package_name
                  || b.package_name_snapshot || "—"} × ${b.deposit_receipt.booking_details?.quantity
                  || b.quantity || 0} ${t("tables")}`}
              />
              <R
                l={t("rentalTotal")}
                v={rp(b.deposit_receipt.booking_details?.rental_total ?? b.final_rental_total)}
              />
            </div>
            <R l={t("depositPaid")} v={rp(b.deposit_receipt.deposit_amount)} />
            <R l={t("deduction")} v={b.deposit_receipt.deduction_amount > 0 ? `-${rp(b.deposit_receipt.deduction_amount)}` : "—"} />
            {b.deposit_receipt.reason && <R l={t("reason")} v={b.deposit_receipt.reason} />}
            <div className="h-px bg-black/10 my-1" />
            <R l={t("refundedToYou")} v={<span className="font-display text-xl text-lm-red" data-testid="receipt-refunded">{rp(b.deposit_receipt.refunded_amount)}</span>} />
            <R l={t("method")} v={b.deposit_receipt.method} />
            <R l={t("refundDate")} v={fmtDate(b.deposit_receipt.returned_date, lang)} />
            {b.deposit_receipt.notes && <p className="text-xs text-ink-soft pt-1">{b.deposit_receipt.notes}</p>}
            {(b.deposit_refund_history || []).length > 1 && (
              <div className="border-t border-black/10 pt-3 mt-2" data-testid="receipt-history">
                <div className="text-[11px] uppercase tracking-widest text-ink-soft">{t("allRefunds")}</div>
                {b.deposit_refund_history.map((r) => (
                  <R key={r.receipt_no + r.created_at} l={`${fmtDate(r.returned_date, lang)} · ${r.receipt_no}`} v={rp(r.refunded_amount)} />
                ))}
                <R l={t("totalRefunded")} v={<span className="font-semibold">{rp(b.deposit_refunded_total || 0)}</span>} />
              </div>
            )}
          </div>
        )}
        {media.length > 0 && (
          <div className="mt-6 lm-card rounded-xl p-6" data-testid="customer-media">
            <h2 className="font-display text-xl tracking-tight">{t("conditionMedia")}</h2>
            <p className="text-xs text-ink-soft mt-1">{t("conditionMediaSub")}</p>
            {["delivery_before", "delivery_after", "pickup_before", "pickup_after", "inspection"].map((st) => {
              const group = media.filter((m) => m.stage === st);
              if (!group.length) return null;
              return (
                <div key={st} className="mt-4" data-testid={`cust-media-${st}`}>
                  <div className="text-[11px] uppercase tracking-widest text-ink-soft">{group[0].stage_label}</div>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {group.map((m) => (
                      <a key={m.id} href={`${API}${m.url.replace("/api", "")}`} target="_blank" rel="noreferrer" className="w-28">
                        {m.media_type === "video" ? (
                          <video src={`${API}${m.url.replace("/api", "")}`} className="w-28 h-20 object-cover rounded-md border border-black/10" muted />
                        ) : (
                          <img src={`${API}${m.url.replace("/api", "")}`} alt={m.notes} className="w-28 h-20 object-cover rounded-md border border-black/10" />
                        )}
                        {m.notes && <span className="block text-[10px] text-ink-soft mt-1 leading-tight">{m.notes}</span>}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}

const R = ({ l, v }) => (
  <div className="flex justify-between gap-4"><span className="text-ink-soft">{l}</span><span className="text-right">{v}</span></div>
);
